// PostHog sink -- wraps the EXISTING analytics.ts PostHog client.
//
// This preserves the current remote pipeline unchanged: the central track()
// choke point just calls into the same initAnalytics()/capture() wrapper the app
// already ships. identify()/reset() stay in analytics.ts and are still called
// directly from useAuth.
//
// ON only in production builds (import.meta.env.PROD), and skipped per-event when
// the user has opted out of remote tracking.

import { analytics } from '@/utils/analytics';
import type { EnrichedEvent } from '../events';
import { isRemoteOptedOut } from '../privacy';
import { isLocalBackendPage } from '@/services/backend';
import type { EventSink } from './types';

let lastSource: string | null = null;

export const posthogSink: EventSink = {
    id: 'posthog',
    // never on a LOCAL review session (services/backend.ts isLocalBackendPage)
    accepts: () => import.meta.env.PROD && !isRemoteOptedOut() && !isLocalBackendPage(),
    send(event: EnrichedEvent) {
        // `source` (human/agent) is a set-once super property so it rides on every
        // event including autocaptured pageviews + session-replay metadata, and a
        // person property ($set) so cohorts segment cleanly. Best practice for a
        // "one dimension, all events" split -- never passed per-call by callers.
        if (event.source !== lastSource) {
            lastSource = event.source;
            analytics.register({ source: event.source });
        }
        // Flatten enrichment + props into PostHog's flat property bag.
        analytics.capture(event.event, {
            source: event.source,
            surface: event.surface,
            app_version: event.app_version,
            session_id: event.session_id,
            $set: { source: event.source },
            ...event.props,
        });
    },
};
